import { useMemo } from 'react';
import type { ExecutorVar } from '~/common/vm/protocol';
import { api } from '~/trpc-extra/api';
import { useProjectId } from '../context/useProjectId';
import { useAtomValue } from 'jotai';
import { executedVariablesAtom } from '~/atoms/recorder-atoms';
import { currentEnvironmentAtom } from '~/atoms/no-code-atoms';
import type { VariableValueInput } from '~/server/api/routers/variable';
import type { OperationParentContext } from '~/server/logic/steps/operation/types';

type VariableScope = 'project' | 'test';

const DEFAULT_SCOPES = new Set<VariableScope>(['test', 'project']);

const getType = (value: VariableValueInput): string => {
  const valueType = typeof value;
  if (valueType === 'object' && value !== null) {
    // Get type of each property
    return `{ ${Object.entries(value)
      .map(([key, val]) => `${JSON.stringify(key)}: ${getType(val)}`)
      .join(', ')} }`;
  }

  return valueType;
};

export const useTestVariables = ({
  testContentId,
  scopes = DEFAULT_SCOPES,
  limit,
  parentContexts,
}: {
  testContentId: string;
  scopes?: Set<VariableScope>;
  limit?: { type: 'operationId'; id: string };
  parentContexts?: OperationParentContext[];
}): ExecutorVar[] => {
  const projectId = useProjectId();

  const currentEnvironment = useAtomValue(currentEnvironmentAtom);

  const { data: testContent } = api.testContent.get.useQuery(
    { id: testContentId },
    { refetchOnMount: false, refetchOnWindowFocus: false },
  );

  // Get the current component ID from parentContexts (the last one is the current component)
  const currentComponentId = parentContexts?.at(-1)?.componentId;

  // Fetch component operations if we're inside a component
  const { data: component } = api.component.get.useQuery(
    { id: currentComponentId! },
    { enabled: !!currentComponentId, refetchOnMount: false, refetchOnWindowFocus: false },
  );

  // Combine test content operations with component operations
  const operations = useMemo(() => {
    const testOps = testContent?.steps.flatMap((step) => step.operations) || [];
    const componentOps = component?.operations || [];
    return [...testOps, ...componentOps];
  }, [testContent, component]);

  const { data: projectVariables = [] } = api.variable.list.useQuery({
    projectId: projectId || '',
    environments: [currentEnvironment, 'ALL'],
  });
  const projectVars: ExecutorVar[] = useMemo(
    () =>
      (projectVariables || [])?.map((projectVariable) =>
        projectVariable.sensitive ?
          {
            name: projectVariable.name,
            valueType: 'STATIC',
            playwrightType: getType(projectVariable.value),
          }
        : {
            name: projectVariable.name,
            value: projectVariable.value,
            valueType: 'STATIC',
            playwrightType: getType(projectVariable.value),
          },
      ),
    [projectVariables],
  );

  const executorVariables = useAtomValue(executedVariablesAtom);

  const executorVariablesWithType = useMemo(
    () =>
      executorVariables.map((variable) => ({
        ...variable,
        playwrightType: getType(variable.value),
      })),
    [executorVariables],
  );

  const projectVarNames = useMemo(() => new Set(projectVars.map((v) => v.name)), [projectVars]);

  // Variables -> type
  const variables = useMemo(() => {
    const limitOperationIndex = limit?.type === 'operationId' ? operations.findIndex(({ id }) => id === limit.id) : -1;
    const operationsToUse = limitOperationIndex !== -1 ? operations.slice(0, limitOperationIndex) : operations;

    const defineExtractedVars: ExecutorVar[] =
      operationsToUse
        .flatMap(({ defineVariableOp }) => defineVariableOp)
        .filter((x) => x !== null)
        // We don't know the type of the variable yet (since it's not executed yet), so we use 'any'
        .map(({ name, type, value }) => ({
          name,
          value,
          valueType: type,
          playwrightType: type === 'STATIC' ? 'string' : getType(value),
        })) || [];

    const emailExtractedVars: ExecutorVar[] =
      operationsToUse
        .flatMap(({ receiveEmailOp }) => receiveEmailOp?.variableExtraction || undefined)
        .filter((x) => x !== undefined)
        // We know the type of the variable must be a string (since it's extracted from an email)
        .map(({ variableName: name }) => ({
          name,
          // We'll always extract a static string
          valueType: 'STATIC',
          playwrightType: 'string',
        })) || [];

    const fetchRequestVars: ExecutorVar[] =
      operationsToUse
        .flatMap(({ fetchRequestOp }) =>
          fetchRequestOp?.responseVariableName ?
            {
              name: fetchRequestOp.responseVariableName,
              valueType: 'DYNAMIC',
              playwrightType: '{ status: number; body: string; bodyAsJson: any }',
            }
          : undefined,
        )
        .filter((x): x is ExecutorVar => x !== undefined) || [];

    // Add parent context parameters if available
    const parentContextsVars: ExecutorVar[] =
      parentContexts?.flatMap((parentContext) =>
        parentContext.componentId && parentContext.parameters ?
          parentContext.parameters.map((parameter) => ({
            name: parameter,
            valueType: 'STATIC',
            playwrightType: 'string',
          }))
        : [],
      ) || [];

    // Filter executed variables to exclude project variables
    const filteredExecutorVariables = executorVariablesWithType.filter((v) => !projectVarNames.has(v.name));

    const mergedVariables = Object.fromEntries(
      [
        ...(scopes.has('project') ? projectVars : []),
        ...(scopes.has('test') ? defineExtractedVars : []),
        ...(scopes.has('test') ? emailExtractedVars : []),
        ...(scopes.has('test') ? fetchRequestVars : []),
        ...filteredExecutorVariables,
        ...parentContextsVars,
      ].map((variable) => [variable.name, variable]),
    );

    return Object.values(mergedVariables);
  }, [
    projectVars,
    projectVarNames,
    operations,
    limit,
    Array.from(scopes).sort(),
    executorVariablesWithType,
    parentContexts,
  ]);

  return variables;
};
